import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { formatCompactCurrency, formatPercent } from '../../../lib/format';
import type { PerformanceTabData } from '../data/api';
import type { MediaKpi } from '../data/types';
import { formatKpiValue } from '../data/utils';
import { CreativeDetailsLink } from '../shared/CreativeDetailsLink';
import { MediaCard } from '../shared/MediaCard';
import type { MediaColumn, MediaTableRow } from '../shared/MediaTable';
import { MediaTable } from '../shared/MediaTable';
import { MediaThumb } from '../shared/MediaThumb';

type CreativeRow = PerformanceTabData['creatives'][number];

export function CreativeTable({
  title,
  creatives,
  kpi,
  currencySymbol,
  accountIds,
}: {
  title: string;
  creatives: CreativeRow[];
  kpi: MediaKpi;
  currencySymbol: string;
  accountIds: readonly number[];
}): ReactNode {
  const columns: MediaColumn[] = [
    { key: 'rank', label: 'Rank', width: '56px' },
    { key: 'media', label: 'Media', width: '1fr' },
    { key: 'spend', label: 'Spend', width: '100px' },
    { key: 'primary', label: kpi.label, width: '100px' },
    { key: 'ctr', label: 'CTR', width: '90px' },
    { key: 'cpm', label: 'CPM', width: '90px' },
  ];

  const rows: MediaTableRow[] = creatives.map((creative, index) => ({
    id: creative.creativeId,
    cells: {
      rank: <Box component="span" sx={{ color: 'text.secondary' }}>{`#${index + 1}`}</Box>,
      media: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <MediaThumb size={100} src={creative.thumbnailUrl} previewUrl={creative.previewUrl} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {creative.creativeName}
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontSize: 10,
                color: 'text.secondary',
                overflowWrap: 'anywhere',
              }}
            >
              {`Creative ID: ${creative.creativeId}`}
            </Typography>
            <CreativeDetailsLink creativeId={creative.creativeId} accountIds={accountIds} />
          </Box>
        </Box>
      ),
      spend: (
        <Box component="span" sx={{ fontWeight: 700 }}>
          {formatCompactCurrency(creative.spend, currencySymbol)}
        </Box>
      ),
      primary: (
        <Box component="span" sx={{ fontWeight: 600 }}>
          {formatKpiValue(creative.kpiValue, kpi, currencySymbol)}
        </Box>
      ),
      ctr: (
        <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {formatPercent(creative.ctr)}
        </Box>
      ),
      cpm: (
        <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {creative.cpm === null ? '-' : `${currencySymbol}${creative.cpm.toFixed(2)}`}
        </Box>
      ),
    },
  }));

  return (
    <MediaCard sx={{ overflow: 'hidden' }}>
      <Typography
        sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary', pt: 4, pb: 2, px: 4 }}
      >
        {title}
      </Typography>
      <MediaTable columns={columns} rows={rows} />
    </MediaCard>
  );
}
